import React, { useState, useMemo, useCallback } from 'react';
import Icon from '../../../components/Icon';
import { Text } from '../../../components/typography';
import styles from './GlobalSearch.module.scss';
import debounce from 'lodash.debounce';
import { loader } from 'graphql.macro';
import { useLazyQuery } from '@apollo/client';
import AsyncSelect from 'react-select/async';

const generateTextForList = (obj) => {
  const displayKeys = [
    'documentString',
    'subject',
    'unitNumber',
    'make',
    'model',
    'name',
    'title',
    'externalId',
    'description',
    'errorMsg',
  ];
  const nestedValues = ['template', 'firstName'];
  const result = Object.entries(obj)
    .map(([key, value]) => {
      let textString = null;

      if (nestedValues.includes(key)) {
        switch (key) {
          case 'template':
            textString = value.title;
            break;
          case 'firstName':
            textString = `${value} ${obj.lastName}`;
            break;
          default:
            break;
        }
      }

      return (
        ((typeof value === 'string' && displayKeys.includes(key)) ||
          textString) &&
        (textString ?? value)
      );
    })
    .filter(Boolean);

  return result.join(' - ');
};

const getIconForList = ({ __Typename: type }) => {
  let icon = 'task_alt';

  switch (type) {
    case 'inspection':
      return { icon: 'inventory', color: '#0d6efd' };
    case 'inspectionTemplate':
      return { icon: 'inventory', color: '#fd7e14' };
    case 'actionItem':
      return { icon, color: '#0d6efd' };
    case 'file':
      return { icon: 'folder', color: '#0d6efd' };
    case 'observation':
      return { icon: 'content_paste_search', color: '#e9222c' };
    case 'incident':
      return { icon: 'fmd_bad', color: '#e9222c' };
    case 'vehicle':
      return { icon: 'local_shipping', color: '#ffc107' };
    case 'expense':
      return { icon: 'paid', color: '#28a745' };
    case 'user':
      return { icon: 'person', color: '#0d6efd' };
    case 'graphqlError':
      return { icon: 'cancel', color: '#e9222c' };
    default:
      return { icon, color: '#000' };
  }
};

const ListOption = (props) => {
  const { innerProps, data } = props;
  const displayText = generateTextForList(data);
  const { icon, color } = getIconForList(data);

  return (
    <div {...innerProps} className={styles.listOption} style={{ color: color }}>
      <Icon>{icon}</Icon>
      <Text noMargin truncate>
        {displayText}
      </Text>
    </div>
  );
};

const globalSearchQuery = loader('./GlobalSearch.graphql');

export default function GlobalSearch() {
  const [runSearch, { loading }] = useLazyQuery(globalSearchQuery);
  const [showSearch, setShowSearch] = useState(false);

  const runGlobalFetch = useCallback(
    async (inputValue) => {
      if (!inputValue) return [];
      const { data, errors } = await runSearch({
        variables: { queryString: inputValue },
      });

      if (errors) {
        let label = 'Error: Timeout';
        if (errors?.[0]?.extensions?.code === 'TIMEOUT') {
          label = 'Error: Search timed out!';
        }
        return [
          {
            searchableValue: null,
            searchableLabel: label,
            errorMsg: 'Search timed out! Try narrowing your search',
            __Typename: 'graphqlError',
          },
        ];
      }

      if (data) {
        return (data?.globalSearch ?? []).map((item) => ({
          searchableValue: `${item.__Typename}:${item.id}`,
          searchableLabel: `${item.id}`,
          ...item,
        }));
      }

      return [];
    },
    [runSearch],
  );
  const debounceLoadOptions = useMemo(
    () =>
      debounce((inputValue, callback) => {
        runGlobalFetch(inputValue).then((options) => {
          callback(options);
        });
      }, 1000),
    [runGlobalFetch],
  );

  return (
    <div className={styles.container}>
      <Icon
        hover
        baseClassName="material-icons-outlined"
        onClick={() => setShowSearch(!showSearch)}
      >
        {showSearch ? 'search_off' : 'search'}
      </Icon>
      {!!showSearch ? (
        <div className={styles.selectorContainer}>
          <AsyncSelect
            className={styles.selector}
            defaultOptions={[]}
            loading={loading}
            getOptionLabel={(o) => o.searchableLabel}
            getOptionValue={(o) => o.searchableValue}
            loadOptions={(inputValue, callback) =>
              debounceLoadOptions(inputValue, callback)
            }
            placeholder="Search for anything…"
            onChange={(selected) => {
              window.location = selected.frontendLink;
            }}
            components={{ Option: ListOption }}
          />
        </div>
      ) : null}
    </div>
  );
}
